var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _update, update_fn;
import { compose, curry, is as is$3, not, isEmpty, toLower, trim, and, when, lens, find as find$1, update, findIndex, propEq, startsWith, view, lensPath, clone, gt, equals, lensProp, isNotNil, over, tap as tap$2, once as once$1 } from "ramda";
import fetch from "cross-fetch";
import { Record, fromJS, List, Map as Map$1, Seq, merge } from "immutable";
const once = (fn) => {
  let done = false;
  let result;
  return function() {
    if (done === false) {
      done = true;
      result = fn.apply(null, arguments);
    }
    return result;
  };
};
const builder = (object) => (data) => {
  const result = Object.keys(object).reduce((acc, key) => {
    const value = data && data[key];
    const validatorName = object[key].name;
    const type = validatorName.replace("is", "").replace("Optional", "");
    return object[key](value) === true ? acc : [...acc, `${key}${!value ? " is required, and" : ""} should be a ${type}`];
  }, []);
  if (result.length > 0) {
    throw new Error(JSON.stringify(result));
  }
  return data;
};
const validators = {
  isString: (string) => typeof string === "string" || string instanceof String,
  isNumber: (number) => typeof number === "number" || number instanceof Number,
  isObject: (object) => typeof object === "object" || object instanceof Object,
  isURL: (string) => {
    try {
      return Boolean(new URL(string));
    } catch (e) {
      return false;
    }
  },
  isHex: (string) => validators.isString(string) && /^[A-F0-9]+$/i.test(string),
  isJsonId: (string) => Boolean(validators.isHex(string) && string.length === 32),
  isOptionalJsonId: (value) => validators.isNullOrUndefined(value) || validators.isJsonId(value),
  isNullOrUndefined: (value) => Boolean(typeof value === "undefined" || value === null),
  isOptionalObject: (value) => validators.isNullOrUndefined(value) || validators.isObject(value),
  isOptionalNumber: (value) => validators.isNullOrUndefined(value) || validators.isNumber(value)
};
const missingConfigErrorMessage = `
  {
    cdnPath: String URL,
    queue: {
      minConsumers: Number,
      maxConsumers: Number,
      itemsPerConsumer: Number
    }
  }
`;
const CDNPathBuilder = (cdnPath) => {
  let roundRobin = 0;
  return () => {
    roundRobin = roundRobin % 3 + 1;
    return roundRobin === 1 ? cdnPath.replace(/#/, "") : cdnPath.replace(/#/, "-" + roundRobin);
  };
};
class Queue {
  constructor() {
    this._items = [];
  }
  push(data) {
    data.forEach((el) => {
      this._items.push(el);
    });
  }
  next() {
    return this._items.shift();
  }
  clear() {
    const items = [].concat([this._items]);
    this._items = [];
    return items;
  }
  size() {
    return this._items.length;
  }
}
class SmartConsumer {
  constructor() {
    __publicField(this, "queue");
    this.keepRunning = false;
  }
  start() {
    this.keepRunning = true;
    this.run();
  }
  run() {
    if (this.keepRunning === false) {
      return;
    }
    const nextItem = this.queue.next();
    if (!nextItem) {
      if (this.queue.consumers.length > this.queue.minConsumers) {
        this.destroy();
      } else {
        this.stop();
      }
      return;
    }
    if (this.queue.actionName) {
      nextItem[this.queue.actionName].call(nextItem, () => {
        setTimeout(() => {
          this.run();
        });
      });
    } else {
      this.queue.action(nextItem, () => {
        setTimeout(() => {
          this.run();
        });
      });
    }
  }
  stop() {
    this.keepRunning = false;
  }
  destroy() {
    this.stop();
    this.queue.removeConsumer(this);
    this.queue = null;
  }
}
class SmartQueue extends Queue {
  constructor(config) {
    super(config);
    __publicField(this, "consumers", null);
    __publicField(this, "maxConsumers", 1);
    __publicField(this, "minConsumers", 1);
    __publicField(this, "itemsPerConsumer", 1);
    __publicField(this, "consumerClass", null);
    this.actionName = config.actionName;
    this.minConsumers = config.minConsumers;
    this.maxConsumers = config.maxConsumers;
    this.itemsPerConsumer = config.itemsPerConsumer;
    this.consumers = [];
    if (this.consumerClass === null) {
      this.consumerClass = SmartConsumer;
    }
    for (let i = 0; i < this.minConsumers; i++) {
      this.instantiateConsumer();
    }
  }
  push(data) {
    super.push(data);
    const consumers = this.consumers.length;
    const items = this._items.length;
    const load = Math.floor(items / this.itemsPerConsumer - consumers);
    const realLoad = Math.min(this.maxConsumers - consumers, load);
    for (let i = 0; i < realLoad; i++) {
      this.instantiateConsumer();
    }
    this.consumers.forEach(function(consumer) {
      if (consumer.keepRunning === false) {
        consumer.start();
      }
    });
  }
  instantiateConsumer() {
    const consumer = new this.consumerClass();
    consumer.queue = this;
    this.consumers.push(consumer);
  }
  removeConsumer(smartConsumer) {
    const consumerIndex = this.consumers.indexOf(smartConsumer);
    this.consumers.splice(consumerIndex, 1);
  }
}
const queueValidator = builder({
  minConsumers: validators.isOptionalNumber,
  maxConsumers: validators.isOptionalNumber,
  itemsPerConsumer: validators.isOptionalNumber
});
const configValidator = builder({
  cdnPath: validators.isURL,
  queue: validators.isOptionalObject,
  environment: validators.isString
});
const setup = once((config) => {
  console.log("Asgard setup: ", config);
  if (validators.isNullOrUndefined(config) === true) {
    throw new Error(missingConfigErrorMessage);
  }
  const parseConfig = configValidator(config);
  const queueConfig = queueValidator(parseConfig.queue) || {};
  const queue = new SmartQueue({
    actionName: "run",
    minConsumers: queueConfig.minConsumers || 1,
    maxConsumers: queueConfig.maxConsumers || 1,
    itemsPerConsumer: queueConfig.itemsPerConsumer || 1
  });
  const environment = parseConfig.environment;
  const getCDNPath2 = CDNPathBuilder(parseConfig.cdnPath);
  return { getCDNPath: getCDNPath2, queue, environment };
});
const getCDNPath$2 = () => setup().getCDNPath();
const pathStructureBuilder = (isHighRes) => (sizeId) => (id) => `/smart_templates/${id}${sizeId ? "/sizes/" + sizeId : ""}${isHighRes ? "/highres" : ""}/structure.json`;
const buildUrlCreator = (getCDNPath2) => (path) => `${getCDNPath2()}${path}`;
const getUrlCreator = buildUrlCreator(getCDNPath$2);
const getRecordPath = (id) => `/smart_templates/${id}/ui.json`;
const getPresetPath = (id) => `/presets/${id}/ui.json`;
const getLayersByAssetPath = (id) => `/image_library/${id}/ui.json`;
const getAssetStructurePath = (id) => `/image_library/${id}/structure.json`;
const getLowResStructurePath = pathStructureBuilder(false);
const getHighResStructurePath = pathStructureBuilder(true);
const getRecordURL = compose(getUrlCreator, getRecordPath);
const getPresetURL = compose(getUrlCreator, getPresetPath);
const getLayersByAssetURL = compose(getUrlCreator, getLayersByAssetPath);
const getAssetStructureURL = compose(getUrlCreator, getAssetStructurePath);
const getLowResStructureURL = compose(getUrlCreator, getLowResStructurePath());
const getHighResStructureURL = compose(getUrlCreator, getHighResStructurePath());
const getLowResStructureURLBySizeCreator = (sizeId) => (id) => compose(getUrlCreator, getLowResStructurePath(sizeId))(id);
const getHighResStructureURLBySizeCreator = (sizeId) => (id) => compose(getUrlCreator, getHighResStructurePath(sizeId))(id);
const tuningUrls = [];
const getTuningURL = (id) => {
  const tuningObj = tuningUrls.find((tuning) => tuning.id === id);
  return tuningObj ? tuningObj.url : false;
};
const toJson$2 = (response) => response.json();
const responseHandler = (response) => response.status === 200 ? Promise.resolve(response) : Promise.reject(response);
const fetcher = (url) => fetch(url).then(responseHandler);
const fetchRecord$1 = compose(fetcher, getRecordURL);
const fetchPreset$1 = compose(fetcher, getPresetURL);
const fetchAssetLayers$1 = compose(fetcher, getLayersByAssetURL);
const fetchAssetStructure$1 = compose(fetcher, getAssetStructureURL);
const fetchLowResStructure$1 = compose(fetcher, getLowResStructureURL);
const fetchHighResStructure$1 = compose(fetcher, getHighResStructureURL);
const fetchLowResStructureBySizeCreator$1 = (sizeId) => compose(fetcher, getLowResStructureURLBySizeCreator(sizeId));
const fetchHighResStructureBySizeCreator$1 = (sizeId) => compose(fetcher, getHighResStructureURLBySizeCreator(sizeId));
const fetchRecords$1 = ({ id, preset }) => Promise.all([
  fetchRecord$1(id).then(toJson$2),
  preset ? fetchPreset$1(preset).then(toJson$2) : Promise.resolve({})
]);
const fetchTuning = (id) => {
  const tuning = getTuningURL(id);
  const isValid2 = validators.isURL(tuning);
  return isValid2 ? fetcher(tuning).then(toJson$2) : Promise.resolve();
};
const composeP = (...ms) => ms.reduce((f, g) => (...args) => g(...args).then(f));
const VALUE$1 = Symbol("Value");
class Container {
  constructor(x) {
    this[VALUE$1] = x;
  }
  map(fn) {
    return fn(this[VALUE$1]);
  }
  static of(x) {
    return new Container(x);
  }
  toString() {
    return `${this.constructor.name}(${this[VALUE$1]})`;
  }
  valueOf() {
    return this[VALUE$1];
  }
}
class Functor extends Container {
  static of(x) {
    return new Functor(x);
  }
  map(fn) {
    return Functor.of(fn(this[VALUE$1]));
  }
}
class Nothing extends Functor {
  isNothing() {
    return true;
  }
  toString() {
    return "Nothing()";
  }
  map(fn) {
    return this;
  }
  orElse(v) {
    return this.isNothing() ? v : this.valueOf();
  }
}
class Just extends Functor {
  isNothing() {
    return false;
  }
  map(fn) {
    return Maybe.of(fn(this[VALUE$1]));
  }
  toString() {
    return "Just()";
  }
  orElse(v) {
    return this.isNothing() ? v : this.valueOf();
  }
}
class Maybe extends Functor {
  constructor(x) {
    super();
    return x === void 0 || x === null ? new Nothing() : new Just(x);
  }
  static of(x) {
    return new Maybe(x);
  }
}
class MaybeArray extends Maybe {
  constructor(x) {
    super();
    return Array.isArray(x) === false ? new Nothing() : new Just(x);
  }
  static of(x) {
    return new MaybeArray(x);
  }
  orElse(v) {
    return this.isNothing() ? v : this.valueOf();
  }
}
class Monad extends Functor {
  static of(x) {
    return new Monad(x);
  }
  map(fn) {
    return Monad.of(fn(this[VALUE$1]));
  }
  unwrap() {
    const myValue = this[VALUE$1];
    return myValue instanceof Container ? myValue.unwrap() : this;
  }
  flatMap(fn) {
    return this.map(fn).unwrap();
  }
  ap(m) {
    return m.map(this.valueOf());
  }
}
const deepCopy$1 = (obj) => {
  let aux = obj;
  if (obj && typeof obj === "object") {
    aux = new obj.constructor();
    Object.getOwnPropertyNames(obj).forEach((prop) => aux[prop] = deepCopy$1(obj[prop]));
  }
  return aux;
};
const is$2 = curry(is$3);
const isString$1 = is$2(String);
const isObject = is$2(Object);
const isArray = is$2(Array);
const isFunction$1 = is$2(Function);
const isLiteralObject = (value) => and(isObject(value), not(isArray(value)));
const isNotEmpty = compose(not, isEmpty);
const isNotEmptyString = (value) => and(isString$1(value), isNotEmpty(value));
const isNotEmptyObject = (value) => and(isObject(value), isNotEmpty(value));
const isValid$1 = (value) => (typeof value === "undefined" || value === null || isEmpty(value)) === false;
const tap$1 = (fn) => (x) => (fn(x), x);
const filter = (predicate) => (iterable) => iterable.filter(predicate);
const find = (predicate) => (iterable) => iterable.find(predicate);
const reduce = (reducer, initialValue) => (iterable) => iterable.reduce(reducer, initialValue);
const mergeByProperties$1 = (properties) => (objA, objB) => {
  if (isValid$1(objA) && isValid$1(objB)) {
    return reduce((obj, property) => (obj[property] = isValid$1(objB[property]) ? deepCopy$1(objB[property]) : obj[property], obj), objA)(properties);
  }
  return objA;
};
const mergeArrayOfObjectsBuilder = (merger2) => (arrayA = [], arrayB = []) => arrayA.map((objA, index) => isValid$1(arrayB[index]) ? merger2(objA, arrayB[index]) : objA);
const uniqueBy = (key) => (iterable) => [...new Map(iterable.map((item) => [item[key], item])).values()];
const toLowerAndTrim = compose(toLower, trim);
const normalizeString = (string) => when(isString$1, toLowerAndTrim, string);
const compareIgnoringCase = (a, b) => normalizeString(a) === normalizeString(b);
const byVisible = (element) => Boolean(element.visible);
const byName = (name) => (element) => element.name === name;
const findByFontName = (name) => find(byName(name));
const filterByVisibility = filter(byVisible);
const createSetterByProperty = (property) => (fn) => (record) => MaybeArray.of(record[property]).map((values) => values.forEach(fn));
const graphicsSetter = createSetterByProperty("graphic");
const backgroundsSetter = createSetterByProperty("background");
const textsSetter = createSetterByProperty("text");
const customRulesSetter = createSetterByProperty("customRules");
const cleanText = (text) => {
  const font = findByFontName(text.font)(text.allowedFonts);
  text.allowedFonts = font ? [font] : [];
};
const cleanLayers = (graphic) => graphic.layers = MaybeArray.of(graphic.layers).map(filterByVisibility).orElse([]);
const cleanCustomRule = (customRule) => delete customRule.accumulatedInteractions;
const cleanGraphics = tap$1(graphicsSetter(cleanLayers));
const cleanBackground = tap$1(backgroundsSetter(cleanLayers));
const cleanTexts = tap$1(textsSetter(cleanText));
const cleanCustomRules = tap$1(customRulesSetter(cleanCustomRule));
const cleanOptions = tap$1(({ options }) => MaybeArray.of(options).map((options2) => options2.forEach((option) => option.options = [])));
const cleanMd5Hash = tap$1((record) => delete record.md5Hash);
const setLayers = (element) => {
  element.layers = element.selectedLayer ? [element.selectedLayer] : [];
  return element;
};
const setGraphics = tap$1(graphicsSetter(setLayers));
const setBackgrounds = tap$1(backgroundsSetter(setLayers));
const setDefaultAssetColors = tap$1((record) => record.defaultAssetColors = MaybeArray.of(record.defaultAssetColors).orElse([]));
const setCustomRules = tap$1((record) => record.customRules = MaybeArray.of(record.customRules).orElse([]));
const normalize$1 = (record) => {
  return Monad.of(record).flatMap(setGraphics).flatMap(setBackgrounds).flatMap(setDefaultAssetColors).flatMap(setCustomRules).valueOf();
};
const clean = (record) => Monad.of(record).flatMap(cleanGraphics).flatMap(cleanBackground).flatMap(cleanTexts).flatMap(cleanCustomRules).flatMap(cleanOptions).flatMap(cleanMd5Hash).valueOf();
const prepare = compose(normalize$1, clean);
const mergeArrayOfObjects = (properties) => mergeArrayOfObjectsBuilder(mergeByProperties$1(properties));
const strategies$1 = () => ({
  graphic: mergeArrayOfObjects(["color", "visible", "preconfiguredSearch", "layers", "selectedLayer"]),
  background: mergeArrayOfObjects(["color", "visible", "preconfiguredSearch", "layers", "selectedLayer"]),
  backgroundColor: mergeByProperties$1(["color", "visible", "layers"]),
  text: mergeArrayOfObjects(["color", "font", "fontFamily", "justification", "capitalization", "contents", "allowedFonts", "sizes", "visible"]),
  size: (a, b) => isValid$1(b) ? deepCopy$1(b) : a,
  previewImage: (a, b) => isValid$1(b) === true ? deepCopy$1(b) : a,
  customRules: (a, b) => isValid$1(b) === true ? deepCopy$1(b) : a,
  defaultAssetColors: (a, b) => isValid$1(b) === true ? deepCopy$1(b) : a,
  composition: (a, b) => isValid$1(a) === true ? mergeByProperties$1(Object.keys(a))(a, b) : null
});
const mergerRecordsByPropertiesBuilder = (strategies2) => ({ ui, preset }) => {
  const createMergerByProperties = (strategies3) => (b) => (property) => tap$1(
    (a) => a[property] = strategies3[property](a[property], b[property])
  );
  const mergeByProperty = createMergerByProperties(strategies2())(JSON.parse(JSON.stringify(preset)));
  const handleNullLayer = (layer) => {
    if (layer.selectedLayer && layer.selectedLayer.id === "null") {
      const selectedLayer = null;
      const layers = [];
      return { ...layer, layers, selectedLayer };
    }
    return layer;
  };
  return Monad.of(JSON.parse(JSON.stringify(ui))).flatMap(mergeByProperty("graphic")).flatMap(mergeByProperty("background")).flatMap(mergeByProperty("backgroundColor")).flatMap(mergeByProperty("text")).flatMap(mergeByProperty("size")).flatMap(mergeByProperty("previewImage")).flatMap(mergeByProperty("customRules")).flatMap(mergeByProperty("defaultAssetColors")).flatMap(mergeByProperty("composition")).flatMap(tap$1((record) => {
    record.graphic = record.graphic.map(handleNullLayer).valueOf();
    record.background = record.background.map(handleNullLayer).valueOf();
  })).valueOf();
};
const definition$4 = {
  assetType: "",
  id: "",
  name: "",
  type: "",
  tags: [],
  nounAssetId: "",
  assetGroups: [],
  hasAnimated: false,
  luminosityCorrection: false,
  usage: null,
  originalFile: "",
  updatedAt: 0,
  isPremium: false,
  objectID: "",
  layers: [],
  image: "",
  path: "",
  visible: true
};
class Asset extends Record(definition$4) {
  static of(asset) {
    return new Asset(asset);
  }
  constructor(asset) {
    const rawData = isFunction$1(asset.toJS) ? asset.toJS() : asset;
    if (rawData.id) {
      rawData.image = `/image_library/${rawData.id}/images/medium.jpg`;
      rawData.path = `image_library/${rawData.id}/image${rawData.type}`;
    }
    super(rawData);
  }
  isDocument() {
    return this.type === "document";
  }
  setLayers(layers) {
    return this.set("layers", isArray(layers) ? layers : []);
  }
  getLayers() {
    return this.get("layers");
  }
  setMainColor(color) {
    return this.setColorByLayerName("main", color);
  }
  setAccentColor(color) {
    return this.setColorByLayerName("accent", color);
  }
  setColorByLayerName(name, color) {
    const hasLayerName = (layer) => compareIgnoringCase(layer.get("name"), name);
    const layers = this.get("layers");
    const newLayers = fromJS(layers).reduce(
      (arr, layer) => hasLayerName(layer) ? arr.push(layer.set("color", color)) : arr.push(layer),
      List()
    ).toJS();
    return this.setLayers(newLayers);
  }
}
const toJson$1 = (response) => response.json();
const getColor = (value) => value.get("color");
const getName = (value) => value.get("name");
const isString = curry(is$3)(String);
const normalize = compose(trim, toLower);
const byAccentName = compose((name) => isString(name) && normalize(name) === "accent", getName);
const byValidColor = compose(isNotEmptyString, getColor);
const getValidAccentLayerFrom = (iterable) => {
  var _a;
  return (_a = fromJS(iterable).toSeq().filter(byAccentName).filter(byValidColor).first()) != null ? _a : Map$1();
};
const mergeLayers = (...args) => Seq(args).reduce((prev, current) => merge(prev, current), Map$1());
const assetLayersMerger = (defaultLayers) => (currentLayers) => (newLayers) => {
  if (isEmpty(newLayers)) {
    return currentLayers;
  }
  const _layers = fromJS(newLayers);
  const accentLayer = getValidAccentLayerFrom(_layers);
  const accentLayerMerged = mergeLayers(
    accentLayer,
    getValidAccentLayerFrom(defaultLayers != null ? defaultLayers : []),
    getValidAccentLayerFrom(currentLayers != null ? currentLayers : [])
  );
  return _layers.set(_layers.indexOf(accentLayer), accentLayerMerged).toJS();
};
const prepareAssetBuilder = (defaultLayers) => {
  return (assetObject) => {
    const asset = Asset.of(assetObject);
    const id = asset.get("id");
    const currentLayers = asset.getLayers();
    const mergeLayers2 = assetLayersMerger(defaultLayers)(currentLayers);
    const setLayers2 = (layers) => asset.setLayers(layers);
    return fetchAssetLayers$1(id).then(toJson$1).then(({ layers }) => layers).then(mergeLayers2).then(setLayers2).catch(() => asset);
  };
};
const setValuesBuilder = (getSetter2) => (initValue, values) => {
  return fromJS(values).reduce((text, value, key) => {
    const setter = getSetter2(key, text);
    return setter ? setter(value) : text;
  }, initValue);
};
const definition$3 = {
  id: 0,
  name: "",
  type: "",
  opacity: 0,
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  visible: true,
  styles: {},
  color: "",
  image: null,
  contents: null,
  font: null,
  smartFilters: null,
  allowColorpicker: true,
  allowSearch: true,
  allowForegroundSearch: false,
  allowBackgroundSearch: false,
  preconfiguredSearch: "",
  groupTags: [],
  displayName: "",
  hasLayers: true,
  excludable: true,
  tag: "",
  selectedLayer: null,
  layers: []
};
const getSetter$2 = (property, context) => {
  const setters = {
    color: context.setColor.bind(context),
    layers: (layers) => context.setAsset(layers.first()),
    selectedLayer: context.setAsset.bind(context)
  };
  return setters[property];
};
const setValues$2 = setValuesBuilder(getSetter$2);
class GB extends Record(definition$3) {
  static of(control) {
    return new GB(control);
  }
  constructor(control) {
    super(control);
  }
  getLayers() {
    return this.get("layers").toJS();
  }
  getAsset() {
    return this.get("selectedLayer").toJS();
  }
  hasSelectedAsset() {
    return isNotEmptyObject(this.get("selectedLayer"));
  }
  hasDocumentAsset() {
    return this.getIn(["selectedLayer", "type"]) === "document";
  }
  getTag() {
    var _a;
    const tag = (_a = this.get("tag")) != null ? _a : "";
    return tag.toLowerCase().trim();
  }
  setValues(values) {
    return setValues$2(this, values);
  }
}
class Background extends GB {
  static of(background) {
    return new Background(background);
  }
  constructor(background) {
    const rawData = isFunction$1(background.toJS) ? background.toJS() : background;
    let { selectedLayer } = rawData;
    selectedLayer = isNotEmptyObject(selectedLayer) ? Asset.of(selectedLayer) : null;
    rawData.layers = selectedLayer ? List([selectedLayer]) : List([]);
    rawData.selectedLayer = selectedLayer;
    super(background);
  }
  setColor(color) {
    return this.set("color", color);
  }
  setAsset(asset) {
    const _asset = isNotEmptyObject(asset) ? Asset.of(asset) : null;
    return this.set("layers", _asset ? List([_asset]) : List([])).set("selectedLayer", _asset);
  }
}
const is$1 = curry(is$3);
const isAsset = is$1(Asset);
const isFunction = is$1(Function);
class Graphic extends GB {
  static of(graphic) {
    return new Graphic(graphic);
  }
  constructor(graphic) {
    const rawData = isFunction(graphic.toJS) ? graphic.toJS() : graphic;
    let { selectedLayer } = rawData;
    selectedLayer = isNotEmptyObject(selectedLayer) ? Asset.of(selectedLayer).setMainColor(rawData.color) : null;
    rawData.layers = selectedLayer ? List([selectedLayer]) : List([]);
    rawData.selectedLayer = selectedLayer;
    super(graphic);
  }
  setColor(color) {
    const result = this.set("color", color);
    if (isAsset(this.selectedLayer) === false) {
      return result;
    }
    const asset = this.selectedLayer.setMainColor(color);
    return result.setAsset(asset);
  }
  setAsset(asset) {
    const _asset = isNotEmptyObject(asset) ? Asset.of(asset).setMainColor(this.color) : null;
    return this.set("layers", _asset ? List([_asset]) : List([])).set("selectedLayer", _asset);
  }
  setValues(values) {
    return super.setValues(
      values.update("color", (current) => {
        var _a;
        return (_a = values.getIn(["colors", "main"])) != null ? _a : current;
      })
    );
  }
}
const definition$2 = {
  id: 0,
  name: "",
  type: "Text",
  opacity: 0,
  fill: 0,
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  visible: true,
  styles: {},
  color: "",
  image: null,
  contents: "",
  font: "",
  fontFamily: "",
  fontSize: 0,
  lineSpacing: 0,
  justification: "",
  capitalization: "",
  baselineShift: 0,
  kerning: 0,
  warpStyle: "",
  warpDirection: "",
  warpHorizontalDistortion: 0,
  warpVerticalDistortion: 0,
  warpBend: 0,
  position: {},
  layers: [],
  smartFilters: null,
  allowColorpicker: true,
  allowMultiline: true,
  maxCharactersPerLine: 0,
  maxLines: 0,
  excludable: true,
  maxCharacters: 0,
  allowedFonts: [],
  allowScale: true,
  allowMove: true,
  tag: ""
};
const getSetter$1 = (property, context) => {
  const setters = {
    contents: context.setContent.bind(context),
    color: context.setColor.bind(context),
    font: context.setFont.bind(context),
    allowedFonts: context.setAllowedFonts.bind(context)
  };
  return setters[property];
};
const setValues$1 = setValuesBuilder(getSetter$1);
class Text extends Record(definition$2) {
  static of(text) {
    return new Text(text);
  }
  constructor(text) {
    text.allowedFonts = uniqueBy("name")(text.allowedFonts);
    super(text);
  }
  setColor(color) {
    return this.set("color", color);
  }
  setFont(font) {
    const fontName = font.get("name");
    const index = this.allowedFonts.findIndex((item) => item.name === fontName);
    const result = this.set("font", fontName);
    return index < 0 ? result.set("allowedFonts", result.allowedFonts.concat([font.set("visible", true)])) : result.setIn([index], font.set("visible", true));
  }
  setContent(content) {
    const isVisible = normalizeString(content).length > 0;
    return this.set("contents", content).set("visible", isVisible);
  }
  setAllowedFonts(fonts) {
    const font = fonts.find((font2) => font2.get("visible") === true);
    let result = this.set("allowedFonts", fonts);
    if (font) {
      result = result.set("font", font.get("name"));
    }
    return result;
  }
  setValues(values) {
    return setValues$1(this, values);
  }
}
const definition$1 = {
  id: 0,
  name: "",
  type: "",
  opacity: 0,
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  visible: true,
  styles: null,
  mask: null,
  color: null,
  image: null,
  contents: null,
  font: null,
  layers: [
    {
      id: 0,
      name: "",
      type: "",
      opacity: 0,
      fill: 0,
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      visible: true,
      styles: {},
      color: null,
      image: "",
      contents: null,
      font: null,
      layers: [],
      smartFilters: null
    }
  ],
  smartFilters: null,
  viewport: null,
  userAgent: null,
  resizeToFill: false,
  tag: ""
};
const getSetter = (property, context) => {
  const setters = {
    hash: context.setHash.bind(context),
    layers: context.setImage.bind(context)
  };
  return setters[property];
};
const setValues = setValuesBuilder(getSetter);
class CustomGraphic extends Record(definition$1) {
  static of(customGraphic) {
    return new CustomGraphic(customGraphic);
  }
  constructor(customGraphic) {
    super(customGraphic);
  }
  setImage(data) {
    return this.setIn(["layers", 0, "image"], data.getIn([0, "image"]));
  }
  setHash(hash) {
    return this.set("hash", hash);
  }
  setValues(values) {
    return setValues(this, values);
  }
}
const definition = {
  previewImage: {},
  highResolution: false,
  migrated: false,
  original: {},
  supportedSizes: [],
  text: [],
  graphic: [],
  background: [],
  backgroundColor: null,
  customGraphic: [],
  uploadRequired: false,
  effect: [],
  id: "",
  version: "",
  forceReload: false,
  useAssetRules: false,
  category: "",
  defaultAssetColors: [],
  customRules: [],
  composition: null
};
const taggable = ["text", "graphic", "customGraphic", "background"];
const is = curry(is$3);
const isBackground = is(Background);
class UIRecord extends Record(definition) {
  static of(record) {
    return new UIRecord(record);
  }
  static toGraphics(graphics) {
    return List(graphics.map(Graphic.of));
  }
  static toBackgrounds(background) {
    return List(background.map(Background.of));
  }
  static toTexts(texts) {
    return List(texts.map(Text.of));
  }
  static toCustomGraphics(customGraphics) {
    return List(customGraphics.map(CustomGraphic.of));
  }
  constructor(record) {
    record.graphic = UIRecord.toGraphics(record.graphic);
    record.background = UIRecord.toBackgrounds(record.background);
    record.text = UIRecord.toTexts(record.text);
    record.customGraphic = UIRecord.toCustomGraphics(record.customGraphic);
    record.backgroundColor = record.backgroundColor ? Background.of(record.backgroundColor) : null;
    super(record);
  }
  getDefaultAssetLayers() {
    return this.defaultAssetColors.map((color) => ({ name: "Accent", color }));
  }
  getGraphics() {
    return this.get("graphic").toJS();
  }
  setGraphics(graphics) {
    return this.set("graphic", UIRecord.toGraphics(graphics));
  }
  getControlByTag(controlTag) {
    const tag = normalizeString(controlTag);
    const type = taggable.find((key) => tag.startsWith(key));
    const byTag = (tag2) => (control) => compareIgnoringCase(control.get("tag"), tag2);
    return this.get(type).find(byTag(tag));
  }
  setBackgrounds(control, index, changes) {
    let result = this;
    if (control) {
      const values = control.setValues(changes);
      result = this.setIn(["background", index], values);
    }
    if (isBackground(this.backgroundColor)) {
      const values = this.backgroundColor.setValues(changes);
      return result.set("backgroundColor", values);
    }
    return result;
  }
  setControlByTag(controlTag, changes) {
    const tag = normalizeString(controlTag);
    const byTag = (tag2) => (control2) => compareIgnoringCase(control2.get("tag"), tag2);
    const findByTag = byTag(tag);
    const type = taggable.find((key) => tag.startsWith(normalizeString(key)));
    const controls = this.get(type);
    const control = controls.find(findByTag);
    const index = controls.findIndex(findByTag);
    if (type === "background") {
      return this.setBackgrounds(control, index, changes);
    }
    return control ? this.setIn([type, index], control.setValues(changes)) : this;
  }
  setValuesByTags(values) {
    return fromJS(values).reduce((acc, value, key) => {
      return acc.setControlByTag(key, value);
    }, this);
  }
}
const mergerRecordsByTags = (ui, values) => {
  return UIRecord.of(ui).setValuesByTags(values).toJS();
};
const setAssetColors = (value) => (layers) => {
  return layers.map((layer) => {
    const color = value.getIn(["colors", toLower(layer.name)]);
    return color ? { ...layer, color } : layer;
  });
};
const mapValues = (ui, values) => {
  const defaultAssetColors = ui.defaultAssetColors.map((color) => ({ name: "Accent", color }));
  const graphics = fromJS(ui.graphic);
  const prepareAsset = prepareAssetBuilder(defaultAssetColors);
  return fromJS(values).toSeq().map((value, key) => {
    var _a;
    const isGraphicAndHasLayerSelected = normalizeString(key).startsWith("graphic") && value.hasIn(["layers", 0, "id"]);
    if (isGraphicAndHasLayerSelected === false) {
      return Promise.resolve(value);
    }
    const graphic = graphics.find((value2) => compareIgnoringCase(value2.get("tag"), key));
    if (!graphic) {
      return Promise.resolve(value);
    }
    const currentLayers = (_a = graphic.getIn(["layers", 0, "layers"])) != null ? _a : List([]);
    const asset = value.setIn(["layers", 0, "layers"], currentLayers).getIn(["layers", 0]);
    return prepareAsset(asset).then((asset2) => asset2.layers).then(setAssetColors(value)).then((layers) => value.setIn(["layers", 0, "layers"], layers));
  }).reduce(
    (acc, promise, key) => acc.then((map) => promise.then((response) => map.set(key, response))),
    Promise.resolve(Map$1())
  ).then((response) => response.toJS());
};
const addItem = (job) => (resolve, reject) => {
  const queue = setup().queue;
  queue.push([{ run: (next) => job().then(resolve).catch(reject).finally(next) }]);
};
const pushToQueue = (job) => (...args) => new Promise(addItem(() => job(...args)));
const mapAssetLayersFromGraphics = (defaultLayers) => (graphicRecords) => {
  const prepareAsset = prepareAssetBuilder(defaultLayers);
  return Promise.all(
    graphicRecords.map((rawGraphic) => {
      const graphic = Graphic.of(rawGraphic);
      return graphic.hasDocumentAsset() ? prepareAsset(graphic.getAsset()).then((asset) => graphic.setIn(["layers", 0], asset).set("selectedLayer", asset)).catch(() => graphic) : Promise.resolve(graphic);
    })
  );
};
const toJS = (collection) => collection.toJS();
const mapAssetLayersFromRecord = (rawRecord) => {
  const record = UIRecord.of(rawRecord);
  const defaultAssetColors = record.getDefaultAssetLayers();
  const graphics = record.getGraphics();
  return mapAssetLayersFromGraphics(defaultAssetColors)(graphics).then((graphics2) => record.set("graphic", graphics2)).then(toJS);
};
const validator = (test) => (value) => test(value);
const stageDataValidator = compose(validator, builder)({
  id: validators.isJsonId,
  preset: validators.isOptionalJsonId
});
const isValid = (value) => Boolean(typeof value === "undefined" || value === null) === false;
const propertiesToMerge = [
  "color",
  "font",
  "fontFamily",
  "justification",
  "capitalization",
  "contents",
  "allowedFonts",
  "sizes",
  "visible"
];
const uniqueByName = uniqueBy("name");
const mergeByProperties = (properties) => (objA = {}, objB = {}) => reduce((obj, property) => {
  obj[property] = Array.isArray(obj[property]) && Array.isArray(objB[property]) ? uniqueByName([...obj[property], ...objB[property]]) : isValid(objB[property]) ? deepCopy$1(objB[property]) : obj[property];
  return obj;
}, objA)(properties);
const strategies = strategies$1();
const merger = mergeByProperties(propertiesToMerge);
strategies.text = mergeArrayOfObjectsBuilder(merger);
const fetchMergeRecords = ({ id, preset }) => fetchRecords$1({ id, preset }).then(([ui, preset2]) => ({ ui: normalize$1(ui), preset: preset2 })).then(mergerRecordsByPropertiesBuilder(() => strategies)).then(mapAssetLayersFromRecord);
const mergeRecords = compose(pushToQueue(fetchMergeRecords), stageDataValidator);
const checkNameListAndUpdate = (layer, allnames) => {
  const nameNoSpaces = layer.name.split(".").pop().replace(/\s+/g, "");
  const found = allnames.indexOf(nameNoSpaces);
  if (found !== -1) {
    const lastNum = Number(nameNoSpaces.split("_").pop()) === NaN ? nameNoSpaces.split("_").pop() : 0;
    const newName = nameNoSpaces + "_" + (lastNum + 1);
    layer.name = newName;
  }
};
const checkBlendingModeFxParent = (layer, propers, parent) => {
  const props = propers || {};
  if (parent !== void 0) {
    props["layerParentProps"] = parent;
  }
  if (Object.entries(props).length > 0) {
    Object.entries(props).forEach((prop) => {
      const [key, value] = prop;
      layer[key] = value;
    });
  }
};
const getProps = (layer, parent) => {
  if (layer.layers.length === 0) {
    return {};
  }
  const props = {};
  if (layer.styles.layerEffects) {
    props.layerFX = layer.styles.layerEffects;
  }
  if (layer.color) {
    props.layerParentProps = {
      name: layer.name,
      x: layer.x,
      y: layer.y,
      width: layer.width,
      height: layer.height,
      opacity: layer.opacity,
      color: layer.color
    };
  }
  if (parent) {
    props.layerParentProps = parent;
  }
  return props;
};
const checkAndPushLayer = (isBackground2, allgraphics, allnames, layer, props, layerParentProps) => {
  if (!isBackground2) {
    if (layerParentProps) {
      checkBlendingModeFxParent(layer, props, layerParentProps);
    } else {
      checkBlendingModeFxParent(layer, props);
    }
  }
  checkNameListAndUpdate(layer, allnames);
  allnames.push(layer.name);
  allgraphics.push(layer);
};
const getLayers = (allgraphics, structure, allnames, parentName, props) => {
  structure.layers.forEach((layer) => {
    if (layer.type === "Folder" && layer.name === "Backgrounds") {
      layer.layers[0].layers[0].smartObject.name = layer.name;
      checkAndPushLayer(true, allgraphics, allnames, layer.layers[0].layers[0].smartObject);
    } else if (layer.type === "Folder" && layer.name === "Safeboxes") {
      getLayers(allgraphics, layer, allnames, layer.name);
    } else if (layer.type === "Folder" || layer.type === "Document") {
      const props2 = getProps(layer);
      if (Object.entries(props2).length > 0) {
        getLayers(allgraphics, layer, allnames, layer.name, props2);
      } else {
        getLayers(allgraphics, layer, allnames, layer.name);
      }
    } else if (layer.type === "Graphic" && layer.smartObject === null) {
      if (layer.name !== "Watermark") {
        checkAndPushLayer(false, allgraphics, allnames, layer, props);
      }
    } else if (layer.type === "Graphic" && layer.smartObject.type === "Document") {
      const layerParentProps = {
        name: parentName ? parentName : layer.name,
        x: layer.x,
        y: layer.y,
        width: layer.width,
        height: layer.height,
        opacity: layer.opacity,
        color: layer.color
      };
      if (layer.smartObject !== null) {
        checkAndPushLayer(false, allgraphics, allnames, layer, props, layerParentProps);
      } else {
        const props2 = getProps(layer, layerParentProps);
        getLayers(allgraphics, layer, allnames, layer.name, props2);
      }
    } else if (layer.type === "SolidColor" && layer.color !== null) {
      layer.name = parentName ? parentName : layer.name;
      checkAndPushLayer(false, allgraphics, allnames, layer, props);
    } else if (layer.type === "Shape") {
      checkAndPushLayer(false, allgraphics, allnames, layer, props);
    } else if (layer.type === "Text") {
      console.log("No texts allowed in this version!!");
    }
  });
};
const getByPath = (data) => (path) => {
  return view(lensPath(path), data);
};
const nameEquals = (name) => propEq(name, "name");
const findByNameLens = (name) => lens(
  (items) => find$1(nameEquals(name))(items),
  (newValue, items) => update(findIndex(nameEquals(name))(items), newValue, items)
);
const startsWithPredicate = (name) => (item) => startsWith(name, toLower(item.name));
const findByStarsWithLens = (name) => lens(
  (items) => find$1(startsWithPredicate(name))(items),
  (newValue, items) => update(findIndex(startsWithPredicate)(items), newValue, items)
);
const isCustomGraphic = (layer) => {
  const layerGetter = getByPath(layer);
  const layerParentPropsName = layerGetter(["layerParentProps", "name"]);
  const layerName = layerGetter(["name"]);
  return layerParentPropsName && (layerParentPropsName.search("nice.custom_graphic.") !== -1 || layerName.search("placeit.replace.userimage") !== -1);
};
const findByName = (name2search, nameProperty, array) => {
  return array.find((obj) => {
    return obj[nameProperty] === name2search;
  });
};
const getLayerName = (layer, ui) => {
  let layerName;
  if (isCustomGraphic(layer)) {
    layerName = layer.name.split(".").pop().replace(/\s+/g, "").replace("Upload_", "");
  } else {
    layerName = layer.name.split(".").pop().replace(/\s+/g, "");
  }
  if (ui) {
    const name2search = layer.layerParentProps ? layer.layerParentProps.name : layer.name;
    let type = name2search.search("nice.custom_graphic.") !== -1 ? "customGraphic" : "graphic";
    const uiLayer = findByName(name2search, "name", ui[type]);
    const hasTag = uiLayer && uiLayer.tag ? true : false;
    layerName = hasTag ? uiLayer.tag.toLowerCase() : layerName;
  }
  return layerName;
};
const getLayerPrefix = (layer) => {
  let layerPrefix = "asset_";
  if (layer.smartObject !== null) {
    layerPrefix = "userAsset_";
  }
  if (layer.name === "Backgrounds") {
    layerPrefix = "userAsset_";
  }
  if (isCustomGraphic(layer)) {
    layerPrefix = "userImage_";
  }
  if (layer.type === "Text") {
    layerPrefix = "userText_";
  }
  return layerPrefix;
};
const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (Math.round(r) << 16) + (Math.round(g) << 8) + Math.round(b)).toString(16).slice(1);
};
const getTransformPoints = (layer) => getByPath(layer)(["smartObject", "transform", "transformPoints"]);
const hasTransformPoints = (layer) => Boolean(getTransformPoints(layer));
const isDimensionsZero = ({
  width,
  height
}) => width === 0 && height === 0;
const isOriginPosition = ({
  x,
  y
}) => x === 0 && y === 0;
const checkDimensionsAndTransformPoints = (layer, layerBase) => Boolean(hasTransformPoints(layer) * isDimensionsZero(layerBase) * isOriginPosition(layerBase));
const getParentProps = (layer) => getByPath(layer)(["layerParentProps"]);
const hasParentProps = (layer) => Boolean(getParentProps(layer));
const checkParentPropsDimensions = (layer) => Boolean(!isDimensionsZero(layer.layerParentProps) * !isOriginPosition(layer.layerParentProps));
const useTransformPointsDimensions = (layer, layerBase) => {
  const tp = getTransformPoints(layer);
  const minLeft = tp[0] < tp[6] ? tp[0] : tp[6];
  const maxRight = tp[2] > tp[4] ? tp[2] : tp[4];
  const minTop = tp[1] < tp[3] ? tp[1] : tp[3];
  const maxBottom = tp[5] > tp[7] ? tp[5] : tp[7];
  layerBase.x = minLeft;
  layerBase.y = minTop;
  layerBase.width = maxRight - minLeft;
  layerBase.height = maxBottom - minTop;
};
const updateValuesWithParent = (layer, values, type) => {
  const extra = type === "userAsset_" && layer.name.search("Backgrounds") === -1 ? 1.25 : 1;
  let layerWidth = layer.width;
  let layerHeight = layer.height;
  let scalex = layer.layerParentProps.width !== 0 ? layer.layerParentProps.width / layer.width : 1;
  let scaley = layer.layerParentProps.height !== 0 ? layer.layerParentProps.height / layer.height : 1;
  if (layer.smartObject !== null && layer.layerParentProps.width !== 0 && layer.layerParentProps.height !== 0) {
    layerWidth = layer.smartObject.width;
    layerHeight = layer.smartObject.height;
    scalex = layer.layerParentProps.width / layer.smartObject.width;
    scaley = layer.layerParentProps.height / layer.smartObject.height;
  } else if (layer.smartObject !== null) {
    layerWidth = layer.smartObject.width;
    layerHeight = layer.smartObject.height;
    scalex = 1;
    scaley = 1;
  }
  const finals = scalex > scaley ? scalex : scaley;
  values.width = layerWidth * finals * extra;
  values.height = layerHeight * finals * extra;
  const finalx = layer.layerParentProps.width !== 0 ? layer.layerParentProps.x + (layer.layerParentProps.width - values.width) / 2 : values.width;
  const finaly = layer.layerParentProps.height !== 0 ? layer.layerParentProps.y + (layer.layerParentProps.height - values.height) / 2 : values.height;
  values.x = finalx;
  values.y = finaly;
};
const getLayerDimensionAndPosition = (layer, prefix) => {
  const values = {
    x: layer.x,
    y: layer.y,
    width: layer.width,
    height: layer.height
  };
  if (hasParentProps(layer) && checkParentPropsDimensions(layer)) {
    updateValuesWithParent(layer, values, prefix);
  }
  if (prefix === "userAsset_" || prefix === "userImage_") {
    if (checkDimensionsAndTransformPoints(layer, values)) {
      useTransformPointsDimensions(layer, values);
    }
  }
  return values;
};
const createSvgPlane = (layer, ui) => {
  const layerName = getLayerName(layer, ui);
  const prefix = getLayerPrefix(layer);
  let layerColor = layer.color;
  if (layer.layerFX && layer.layerFX.solidFill) {
    const colorObj = layer.layerFX.solidFill.color;
    layerColor = rgbToHex(colorObj.red, colorObj.green, colorObj.blue);
  }
  const positionDimensionValues = getLayerDimensionAndPosition(layer, prefix);
  const rectEl = `<svg xmlns="http://www.w3.org/2000/svg" width="${positionDimensionValues.width}" height="${positionDimensionValues.height}" x="${positionDimensionValues.x}" y="${positionDimensionValues.y}" viewBox="${positionDimensionValues.x} ${positionDimensionValues.y} ${positionDimensionValues.width} ${positionDimensionValues.height}" style="--userColor_${layerName}:${layerColor}"><g><rect width="${positionDimensionValues.width}" height="${positionDimensionValues.height}" x="${positionDimensionValues.x}" y="${positionDimensionValues.y}" fill="var(--userColor_${layerName})"/></g></svg>`;
  return rectEl;
};
const getLayerAsset = (layer, ui, uiLayer) => {
  let layerAsset = layer.image;
  if (uiLayer) {
    if (layer.name === "Backgrounds" || layer.smartObject) {
      layerAsset = uiLayer ? uiLayer.selectedLayer : null;
    }
    if (isCustomGraphic(layer)) {
      const imgUrl = uiLayer ? uiLayer.layers[0].image : layer.image;
      layerAsset = [{
        image: imgUrl,
        visible: true
      }];
    }
  } else {
    if (isCustomGraphic(layer) || layer.name === "Backgrounds") {
      layerAsset = "";
    }
    if (!layer.smartObject && (layer.type === "SolidColor" || layer.layerParentProps && layer.layerParentProps.color && layer.layerParentProps.color !== "#000000" || layer.layerFX && layer.layerFX.solidFill)) {
      layerAsset = createSvgPlane(layer, ui);
    }
  }
  return layerAsset;
};
const camelToDashes = (string) => {
  return string.replace(/[A-Z]/g, function(letter) {
    return `-${letter.toLowerCase()}`;
  });
};
const createColor = (layer, ui) => {
  const layerName = getLayerName(layer, ui);
  let layerColor = layer.color;
  if (layer.layerFX && layer.layerFX.solidFill) {
    const colorObj = layer.layerFX.solidFill.color;
    layerColor = rgbToHex(colorObj.red, colorObj.green, colorObj.blue);
  }
  const colorEl = {};
  colorEl["userColor_" + layerName] = layerColor;
  return colorEl;
};
const createStructLayer = (layer, ui) => {
  const layerName = getLayerName(layer, ui);
  const prefix = getLayerPrefix(layer);
  const layerAsset = getLayerAsset(layer, ui);
  const layerOpacity = 1;
  const positionDimensionValues = getLayerDimensionAndPosition(layer, prefix);
  const layerBase = {
    type: "graphic",
    name: prefix + layerName,
    width: positionDimensionValues.width,
    height: positionDimensionValues.height,
    x: positionDimensionValues.x,
    y: positionDimensionValues.y,
    opacity: layerOpacity,
    asset: layerAsset
  };
  if (layer.type === "SolidColor" || layer.layerParentProps && layer.layerParentProps.color && layer.layerParentProps.color !== "#000000") {
    layerBase.colors = createColor(layer, ui);
  }
  if (layer.type === "Text") {
    delete layerBase.asset;
    layerBase.type = "text";
    layerBase.fontSize = layer.fontSize;
    layerBase.fontStyle = "normal";
    layerBase.fontWeight = "normal";
    layerBase.letterSpacing = 0;
    layerBase.lineHeight = layer.fontSize * 1.25;
  }
  return layerBase;
};
const createStructureV5 = (structure, ui, layers) => {
  const videoWidth = structure.width % 2 === 0 ? structure.width : structure.width - 1;
  const videoHeight = structure.height % 2 === 0 ? structure.height : structure.height - 1;
  const structureBase = {
    width: videoWidth,
    height: videoHeight,
    framerate: 24,
    layers: []
  };
  layers.forEach((layer) => {
    const layerBase = createStructLayer(layer, ui);
    structureBase.layers.push(layerBase);
  });
  structureBase.layers.reverse();
  return structureBase;
};
const getAngle = (points) => {
  const middlePoint = points[0];
  const xPoint = points[1];
  const yPoint = points[2];
  const xCoor = [xPoint[0] - middlePoint[0], xPoint[1] - middlePoint[1]];
  const yCoor = [yPoint[0] - middlePoint[0], yPoint[1] - middlePoint[1]];
  const xCooryCoor = xCoor[0] * yCoor[0] + xCoor[1] * yCoor[1];
  const xCoorYCoor = Math.sqrt(xCoor[0] ** 2 + yCoor[0] ** 2) * Math.sqrt(xCoor[1] ** 2 + yCoor[1] ** 2);
  const cosT = xCooryCoor / xCoorYCoor;
  const aCos = Math.acos(cosT);
  return aCos / Math.PI * 180;
};
const getRotationXY = (points) => {
  const aPoints = [
    [points[0], points[1]],
    [points[2], points[3]],
    [points[6], points[7]]
  ];
  const bPoints = [
    [points[2], points[3]],
    [points[0], points[1]],
    [points[4], points[5]]
  ];
  const dPoints = [
    [points[6], points[7]],
    [points[4], points[5]],
    [points[0], points[1]]
  ];
  const aAngle = getAngle(aPoints);
  const bAngle = getAngle(bPoints);
  const dAngle = getAngle(dPoints);
  const rotationY = -(aAngle - bAngle) / 2;
  const rotationX = (aAngle - dAngle) / 2;
  return {
    x: rotationX,
    y: rotationY
  };
};
const getUiLayer = (layer, ui) => {
  let lName = layer.name;
  if (layer.layerParentProps) {
    lName = layer.layerParentProps.name;
  } else if (layer.name === "Backgrounds") {
    lName = "Background";
  }
  let uiLayer = null;
  if (lName === "Background Color") {
    uiLayer = ui["backgroundColor"];
    return uiLayer;
  }
  const layerTypes = ["text", "graphic", "background", "customGraphic"];
  for (let type of layerTypes) {
    const layer2update = ui[type].find(function(ulayer) {
      return ulayer.name === lName;
    });
    if (layer2update) {
      uiLayer = layer2update;
      break;
    }
  }
  return uiLayer;
};
const createTween = (layer) => {
  if (isCustomGraphic(layer) === false && layer.name !== "Backgrounds") {
    if (!layer.styles || layer.styles.blendOptions.mode === "passThrough") {
      return [];
    }
  }
  if (layer.layerFX && layer.layerFX.solidFill) {
    return [];
  }
  const tweenBase = {
    name: "position tween",
    ease: "M0,0C0.3,0.3,0.7,0.7,1,1",
    position: 0,
    duration: 1e-3,
    values: {}
  };
  if (layer.name === "Backgrounds") {
    let rotXY = {
      x: 0,
      y: 0
    };
    let rotZ = 0;
    if (layer.smartObject && layer.smartObject.transform && layer.smartObject.transform.rotationAngle) {
      rotZ = layer.smartObject.transform.rotationAngle;
    }
    if (layer.smartObject && layer.smartObject.transform && layer.smartObject.transform.transformPoints) {
      rotXY = getRotationXY(layer.smartObject.transform.transformPoints);
    }
    tweenBase.values = {
      transformPerspective: [60],
      transformOriginX: ["50%"],
      transformOriginY: ["50%"],
      transformOriginZ: ["0%"],
      rotationX: [rotXY.x],
      rotationY: [rotXY.y],
      rotationZ: [rotZ],
      scaleX: [1],
      scaleY: [1],
      x: [0],
      y: [0]
    };
  }
  const styles = layer.styles ? layer.styles : layer.layers[0].styles;
  if (styles.blendOptions.mode && styles.blendOptions.mode !== "passThrough") {
    const snakeCase = camelToDashes(styles.blendOptions.mode);
    tweenBase.values.mixBlendMode = [snakeCase];
  }
  if (styles.blendOptions.opacity) {
    tweenBase.values.opacity = [styles.blendOptions.opacity / 100];
  }
  if (layer.layerParentProps) {
    tweenBase.values.opacity = [layer.layerParentProps.opacity / 100];
  }
  return [tweenBase];
};
const createUiAssetLayer = (layer, ui) => {
  const layerName = getLayerName(layer, ui);
  const layerTween = createTween(layer);
  const prefix = getLayerPrefix(layer);
  const positionDimensionValues = getLayerDimensionAndPosition(layer, prefix);
  const layer2updateFrom = getUiLayer(layer, ui);
  const layerAsset = getLayerAsset(layer, ui, layer2updateFrom);
  if (isLiteralObject(layerAsset)) {
    layerAsset.assetType = "image";
  }
  let layerBase = {
    allowColorpicker: false,
    allowCustomUpload: false,
    allowPositioning: false,
    allowScaling: false,
    allowSearch: false,
    allowTrimmer: false,
    animationData: {
      type: "custom",
      id: "",
      animations: layerTween
    },
    color: "",
    excludable: false,
    groupTags: "",
    hasLayers: false,
    height: positionDimensionValues.height,
    layerName: prefix + layerName,
    name: layerName,
    opacity: 1,
    preconfiguredSearch: "",
    selectedLayer: null,
    startTime: 0,
    timelineEnd: 5,
    timelineStart: 0,
    visible: true,
    width: positionDimensionValues.width,
    x: positionDimensionValues.x,
    y: positionDimensionValues.y
  };
  if (prefix === "userAsset_") {
    layerBase.allowCustomUpload = true;
    layerBase.allowSearch = true;
    layerBase.allowTrimmer = true;
    layerBase.excludable = true;
    layerBase.hasLayers = true;
    layerBase.selectedLayer = layerAsset;
  }
  if (prefix === "userImage_") {
    delete layerBase.allowColorpicker;
    delete layerBase.allowCustomUpload;
    delete layerBase.allowSearch;
    delete layerBase.allowTrimmer;
    delete layerBase.groupTags;
    delete layerBase.hasLayers;
    delete layerBase.preconfiguredSearch;
    delete layerBase.selectedLayer;
    delete layerBase.startTime;
    layerBase.hash = "";
    layerBase.layers = layerAsset;
  }
  if (layer.type === "Text") {
    delete layerBase.allowColorpicker;
    delete layerBase.allowCustomUpload;
    delete layerBase.allowSearch;
    delete layerBase.allowTrimmer;
    delete layerBase.groupTags;
    delete layerBase.hasLayers;
    delete layerBase.preconfiguredSearch;
    delete layerBase.selectedLayer;
    delete layerBase.startTime;
    layerBase.allowedFonts = layer2updateFrom ? layer2updateFrom.allowedFonts : [];
    layerBase.color = layer2updateFrom ? layer2updateFrom.color : layer.color;
    layerBase.contents = layer2updateFrom ? layer2updateFrom.contents : layer.contents;
    layerBase.excludable = true;
    layerBase.font = layer2updateFrom ? layer2updateFrom.font : layer.font;
    layerBase.height = layer.safebox ? layer.safebox.height : 0;
    layerBase.justification = layer2updateFrom ? layer2updateFrom.justification.toLowerCase() : layer.justification.toLowerCase();
    layerBase.maxCharacters = 30;
    layerBase.maxLines = 1;
    layerBase.textTransform = "none";
    layerBase.width = layer.safebox ? layer.safebox.width : 0;
    layerBase.x = layer.safebox ? layer.safebox.x : 0;
    layerBase.y = layer.safebox ? layer.safebox.y : 0;
  }
  return layerBase;
};
const createUiColorLayer = (layer, ui) => {
  const layer2updateFrom = getUiLayer(layer, ui);
  if (layer.type !== "SolidColor" && !layer.layerFX) {
    return;
  }
  if (layer.type === "SolidColor" && layer.color === null) {
    return;
  }
  if (layer.color === null && !layer.layerFX.hasOwnProperty("solidFill")) {
    return;
  }
  const layerName = getLayerName(layer, ui);
  if (layerName.match(/^replace/) !== null) {
    return;
  }
  const layerBase = {
    name: layerName,
    layerName: "asset_" + layerName,
    colorId: "userColor_" + layerName,
    color: layer.color
  };
  if (layer.layerFX && layer.layerFX.solidFill) {
    const colorObj = layer.layerFX.solidFill.color;
    layerBase.color = rgbToHex(colorObj.red, colorObj.green, colorObj.blue);
  }
  if (layer2updateFrom) {
    layerBase.color = layer2updateFrom.color;
  }
  return layerBase;
};
const createUiV5 = (ui, layers) => {
  const uiSizes = ui.original.size;
  const videoHighWidth = uiSizes.high.width % 2 === 0 ? uiSizes.high.width : uiSizes.high.width - 1;
  const videoHighHeight = uiSizes.high.height % 2 === 0 ? uiSizes.high.height : uiSizes.high.height - 1;
  const videoLowWidth = uiSizes.preview.width % 2 === 0 ? uiSizes.preview.width : uiSizes.preview.width - 1;
  const videoLowHeight = uiSizes.preview.height % 2 === 0 ? uiSizes.preview.height : uiSizes.preview.height - 1;
  const uiBase = {
    id: ui.id,
    text: [],
    asset_library: [],
    images: [],
    color_picker: [],
    original: {
      name: "Default",
      size: {
        high: {
          width: videoHighWidth,
          height: videoHighHeight
        },
        preview: {
          width: videoLowWidth,
          height: videoLowHeight
        }
      }
    },
    key_time: 0.04
  };
  layers.forEach((layer) => {
    const layerBase = createUiAssetLayer(layer, ui);
    if (layer.font) {
      uiBase.text.push(layerBase);
    } else if ("layers" in layerBase) {
      uiBase.images.push(layerBase);
    } else {
      uiBase.asset_library.push(layerBase);
    }
  });
  layers.forEach((layer) => {
    const colorBase = createUiColorLayer(layer, ui);
    if (colorBase) {
      uiBase.color_picker.push(colorBase);
    }
  });
  uiBase.asset_library.reverse();
  return uiBase;
};
const getCDNPath$1 = () => setup().getCDNPath();
const setFullPathAssets = (ui, structure) => {
  if (ui === void 0) {
    throw new Error("UI/Record, is necessary to get the ID.");
  }
  if (!ui.id) {
    throw new Error("UI/Record, is not valid.");
  }
  const startUrl = getCDNPath$1();
  structure.layers.forEach((layer) => {
    if (layer.asset && layer.asset.search("assets/") !== -1) {
      layer.asset = `${startUrl}/smart_templates/${ui.id}/${layer.asset}`;
    }
  });
  ui.images.forEach((image) => {
    if (image.layers && image.layers[0].image.search("assets/") !== -1) {
      image.layers[0].image = `${startUrl}/smart_templates/${ui.id}/${image.layers[0].image}`;
    }
  });
};
const mergeTransforms = (ui, tuning) => {
  tuning.forEach((tune) => {
    let selected = findByName(tune.layerName, "layerName", ui["asset_library"]);
    selected = selected ? selected : findByName(tune.layerName, "layerName", ui["images"]);
    if (selected) {
      selected.animationData.animations = tune.animations;
    }
  });
};
const getAdjugate = (m) => {
  return [
    m[4] * m[8] - m[5] * m[7],
    m[2] * m[7] - m[1] * m[8],
    m[1] * m[5] - m[2] * m[4],
    m[5] * m[6] - m[3] * m[8],
    m[0] * m[8] - m[2] * m[6],
    m[2] * m[3] - m[0] * m[5],
    m[3] * m[7] - m[4] * m[6],
    m[1] * m[6] - m[0] * m[7],
    m[0] * m[4] - m[1] * m[3]
  ];
};
const multiplyMatrixByMatrix = (a, b) => {
  var c = Array(9);
  for (var i = 0; i !== 3; ++i) {
    for (var j = 0; j !== 3; ++j) {
      var cij = 0;
      for (var k = 0; k !== 3; ++k) {
        cij += a[3 * i + k] * b[3 * k + j];
      }
      c[3 * i + j] = cij;
    }
  }
  return c;
};
const multiplyMatrixByVector = (m, v) => {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2]
  ];
};
const basisToPoints = (x1, y1, x2, y2, x3, y3, x4, y4) => {
  var m = [
    x1,
    x2,
    x3,
    y1,
    y2,
    y3,
    1,
    1,
    1
  ];
  var v = multiplyMatrixByVector(getAdjugate(m), [x4, y4, 1]);
  return multiplyMatrixByMatrix(m, [
    v[0],
    0,
    0,
    0,
    v[1],
    0,
    0,
    0,
    v[2]
  ]);
};
const general2DProjection = (x1s, y1s, x1d, y1d, x2s, y2s, x2d, y2d, x3s, y3s, x3d, y3d, x4s, y4s, x4d, y4d) => {
  var s = basisToPoints(x1s, y1s, x2s, y2s, x3s, y3s, x4s, y4s);
  var d = basisToPoints(x1d, y1d, x2d, y2d, x3d, y3d, x4d, y4d);
  return multiplyMatrixByMatrix(d, getAdjugate(s));
};
const transform2d = (elt, x1, y1, x2, y2, x3, y3, x4, y4) => {
  var tlc = elt.oCoords;
  var t = general2DProjection(tlc.tl[0], tlc.tl[1], x1, y1, tlc.tr[0], tlc.tr[1], x2, y2, tlc.bl[0], tlc.bl[1], x3, y3, tlc.br[0], tlc.br[1], x4, y4);
  for (var i = 0; i !== 9; ++i) {
    t[i] = t[i] / t[8];
    if (t[i] > 0 && t[i] < 1e-6) {
      t[i] = 0;
    } else if (t[i] < 0 && t[i] > -1e-6) {
      t[i] = 0;
    }
  }
  t = [
    t[0],
    t[3],
    0,
    t[6],
    t[1],
    t[4],
    0,
    t[7],
    0,
    0,
    1,
    0,
    t[2],
    t[5],
    0,
    t[8]
  ];
  t = "matrix3d(" + t.join(", ") + ")";
  return {
    matrix3d: t,
    transformOrigin: elt.transformOrigin
  };
};
const transformer = (config) => {
  var tCoords = config.targetCoords;
  var targetElement = config.targetElement;
  return transform2d(targetElement, tCoords.tl[0], tCoords.tl[1], tCoords.tr[0], tCoords.tr[1], tCoords.bl[0], tCoords.bl[1], tCoords.br[0], tCoords.br[1]);
};
const getTransforms = (videoLayers, layersWithTransformPoints) => {
  const tunings = {
    customGraphics: []
  };
  layersWithTransformPoints.forEach((layerWithTransformPoints) => {
    const layerToTransform = videoLayers.layers.find((layer) => layer.name === layerWithTransformPoints.layerName);
    const transformPoints = layerWithTransformPoints.transformPoints;
    const config = {
      targetCoords: {
        tl: [transformPoints[0], transformPoints[1]],
        tr: [transformPoints[2], transformPoints[3]],
        bl: [transformPoints[6], transformPoints[7]],
        br: [transformPoints[4], transformPoints[5]]
      },
      targetElement: {
        transformOrigin: `${layerToTransform.x * -1}px ${layerToTransform.y * -1}px`,
        oCoords: {
          tl: [layerToTransform.x, layerToTransform.y],
          tr: [layerToTransform.x + layerToTransform.width, layerToTransform.y],
          bl: [layerToTransform.x, layerToTransform.y + layerToTransform.height],
          br: [layerToTransform.x + layerToTransform.width, layerToTransform.y + layerToTransform.height]
        }
      }
    };
    const transforms = transformer(config);
    tunings.customGraphics.push({
      layerName: layerWithTransformPoints.layerName,
      transforms
    });
  });
  return tunings;
};
const transformPointsMapper = (ui) => (layer) => {
  const layerName = getLayerName(layer, ui);
  const prefix = getLayerPrefix(layer);
  const transformPoints = getTransformPoints(layer);
  return {
    layerName: prefix + layerName,
    transformPoints
  };
};
const getCustomGraphicFromLayers = (layers) => layers.filter(isCustomGraphic);
const getCustomGraphicTransformPoints = (ui, layers) => {
  return getCustomGraphicFromLayers(layers).map(transformPointsMapper(ui));
};
const scaleTransformPoints = (layers, scaleFactor) => {
  return layers.map((layer) => {
    const transformPoint = {
      layerName: layer.layerName,
      transformPoints: []
    };
    layer.transformPoints.forEach((tp) => {
      const scaledPoint = tp * scaleFactor;
      transformPoint.transformPoints.push(scaledPoint);
    });
    return transformPoint;
  });
};
const getCDNPath = () => setup().getCDNPath();
const applyTexts = (slide, _structureJsonCopy) => {
  let layer, font;
  const startUrl = getCDNPath();
  slide.text.forEach(function(text) {
    layer = findByName(text.layerName, "name", _structureJsonCopy.layers);
    font = findByName(text.font, "name", text.allowedFonts);
    layer.isVisible = text.visible;
    layer.contents = text.contents;
    layer.fillColor = text.color;
    layer.fontUri = startUrl + font.file;
    layer.fontFamily = text.font;
    layer.animations = text.animationData.animations;
    layer.timelineStart = text.timelineStart;
    layer.timelineEnd = text.timelineEnd;
    layer.justification = text.justification;
    layer.width = text.width;
    layer.height = text.height;
    layer.x = text.x;
    layer.y = text.y;
    layer.opacity = text.opacity;
  });
};
const applyCustomImages = (slide, _structureJsonCopy) => {
  let layer;
  slide.images.forEach(function(customImage) {
    layer = findByName(customImage.layerName, "name", _structureJsonCopy.layers);
    layer.animations = customImage.animationData.animations;
    layer.timelineStart = customImage.timelineStart;
    layer.timelineEnd = customImage.timelineEnd;
    layer.width = customImage.width;
    layer.height = customImage.height;
    layer.x = customImage.x;
    layer.y = customImage.y;
    layer.opacity = customImage.opacity;
    if (customImage.layers.length > 0) {
      layer.isVisible = customImage.layers[0].visible;
      layer.asset = customImage.layers[0].image || layer.asset;
    }
  });
};
const getAssetUri = (selectedAsset) => {
  var isCustomUpload = selectedAsset.id === "customGraphic";
  var isImage = selectedAsset.assetType === "image";
  var isVideo = selectedAsset.assetType === "video";
  const startUrl = getCDNPath();
  if (isCustomUpload && isVideo) {
    return selectedAsset.video;
  }
  if (isCustomUpload && isImage) {
    return selectedAsset.image;
  }
  if (isVideo) {
    return startUrl + selectedAsset.video;
  }
  if (isImage) {
    return startUrl + "/" + selectedAsset.path;
  }
};
const applyAssetLibrary = (slide, _structureJsonCopy) => {
  let layer, selected;
  slide.asset_library.forEach(function(asset) {
    layer = findByName(asset.layerName, "name", _structureJsonCopy.layers);
    selected = asset.selectedLayer;
    layer.isVisible = asset.visible;
    layer.animations = asset.animationData.animations;
    layer.timelineStart = asset.timelineStart;
    layer.timelineEnd = asset.timelineEnd;
    layer.width = asset.width;
    layer.height = asset.height;
    layer.x = asset.x;
    layer.y = asset.y;
    layer.opacity = asset.opacity;
    if (selected && selected.assetType === "video") {
      delete layer.asset;
      layer.startTime = asset.startTime || 0;
      layer.type = "video";
      layer.assetUri = getAssetUri(selected);
    }
    if (selected && selected.assetType === "image") {
      delete layer.assetUri;
      delete layer.startTime;
      layer.type = "graphic";
      layer.asset = getAssetUri(selected);
    }
  });
};
const applyColors = (slide, _structureJsonCopy) => {
  let layer;
  slide.color_picker.forEach(function(color) {
    layer = findByName(color.layerName, "name", _structureJsonCopy.layers);
    if (!layer.colors) {
      layer.colors = [];
    }
    layer.colors[color.colorId] = color.color;
  });
};
const getTotalDuration = () => {
  var duration = 0, layerEnd, animationEnd;
  globalThis._structureJsonCopy.layers.forEach(function(layer) {
    layerEnd = layer.timelineEnd;
    layer.animations.forEach(function(animation) {
      animationEnd = animation.position + animation.duration;
      if (animationEnd > layerEnd) {
        layerEnd = animationEnd;
      }
    });
    if (layerEnd > duration) {
      duration = layerEnd;
    }
  });
  return duration;
};
const applyAudio = (collection, _structureJsonCopy) => {
  let audio;
  const startUrl = getCDNPath();
  _structureJsonCopy.audio = [];
  audio = _structureJsonCopy.audio;
  collection.audio.forEach(function(audioRecord) {
    if (audioRecord.track != null) {
      audio.push({
        name: audioRecord.audioName,
        assetUri: startUrl + audioRecord.track.audio,
        isMuted: !audioRecord.enabled,
        timelineStart: audioRecord.startTime,
        timelineEnd: audioRecord.timelineEnd || getTotalDuration(),
        startTime: audioRecord.timelineStart
      });
    }
  });
};
const mergeV5 = (config) => {
  const _structureJsonCopy = JSON.parse(JSON.stringify(config.structureJson));
  config.collection.slides.forEach(function(slide) {
    applyTexts(slide, _structureJsonCopy);
    applyAssetLibrary(slide, _structureJsonCopy);
    applyCustomImages(slide, _structureJsonCopy);
    applyColors(slide, _structureJsonCopy);
  });
  applyAudio(config.collection, _structureJsonCopy);
  return _structureJsonCopy;
};
const _roundNumber = (value) => {
  return Number(Math.round(value + "e" + 2) + "e-" + 2);
};
const _getScaledValue = (value, scale, roundValue) => {
  let scaledValue = value * scale;
  if (roundValue === true) {
    return _roundNumber(scaledValue);
  }
  return scaledValue;
};
const scaleV5 = (object, scale, roundValue) => {
  const keysToScale = ["width", "height", "x", "y"];
  Object.keys(object).forEach(function(key) {
    const val = object[key];
    const isArray2 = Array.isArray(val);
    const isObject2 = typeof val === "object" && val !== null;
    const isKeyToScale = keysToScale.some(function(keyToScale) {
      return keyToScale === key;
    });
    if (key === "original") {
      return;
    }
    if (isKeyToScale && isArray2) {
      object[key] = val.map(function(value) {
        return _getScaledValue(value, scale, roundValue);
      });
      return;
    }
    if (isKeyToScale) {
      object[key] = _getScaledValue(val, scale, roundValue);
      return;
    }
    if (isArray2) {
      val.forEach(function(obj) {
        scaleV5(obj, scale, roundValue);
      });
      return;
    }
    if (isObject2) {
      scaleV5(val, scale, roundValue);
      return;
    }
  });
};
const getEnvironment = () => setup().environment;
const ConverterV5 = (data, width, transform) => {
  const allnames = [];
  const allgraphics = [];
  const widthSize = width || data.structure.width;
  getLayers(allgraphics, data.structure, allnames);
  if (allgraphics[allgraphics.length - 1].name === "Backgrounds") {
    allgraphics.pop();
  }
  const structv5 = createStructureV5(data.structure, data.record, allgraphics);
  const uiv5 = createUiV5(data.record, allgraphics);
  if (transform) {
    mergeTransforms(uiv5, transform);
  }
  setFullPathAssets(uiv5, structv5);
  const collection = {
    isHighRes: true,
    slides: [uiv5],
    audio: [{
      name: "Audio Track",
      audioName: "Audio1",
      enabled: true,
      timelineStart: "0",
      timelineEnd: "5",
      startTime: "0",
      track: null
    }]
  };
  const videoLayers = mergeV5({
    structureJson: structv5,
    collection,
    environment: getEnvironment()
  });
  const videoLayersScaled = clone(videoLayers);
  const scaleFactor = widthSize / videoLayers.width;
  if (scaleFactor !== 1) {
    scaleV5(videoLayersScaled, scaleFactor);
  }
  const customGraphicTransformPoints = getCustomGraphicTransformPoints(data.record, allgraphics);
  const customGraphicTransformPointsScaled = scaleTransformPoints(customGraphicTransformPoints, scaleFactor);
  const customGraphicTransforms = getTransforms(videoLayers, customGraphicTransformPoints);
  const customGraphicTransformsScaled = getTransforms(videoLayersScaled, customGraphicTransformPointsScaled);
  return {
    collection,
    structureJson: structv5,
    tuning: customGraphicTransforms,
    tuningScaled: customGraphicTransformsScaled,
    videoLayers,
    videoLayersScaled
  };
};
const isNumber = (value) => is$3(Number, value);
const areNumbers = (width, height) => and(isNumber(width), isNumber(height));
const isValidNumber = (value) => value > 0;
const isLandscape = (width, height) => areNumbers(width, height) ? gt(width, height) : false;
const isSquare = (width, height) => areNumbers(width, height) ? equals(width, height) : false;
const isPortrait = (width, height) => areNumbers(width, height) ? gt(height, width) : false;
const calcScaledWidth = (dimensions) => {
  const { originalWidth, originalHeight, width, height } = dimensions;
  if (and(isValidNumber(width)), not(isValidNumber(height))) {
    return width;
  }
  const changeToPortrait = isPortrait(width, height);
  if (isLandscape(originalWidth, originalHeight) && changeToPortrait) {
    return originalWidth * height / originalHeight;
  }
  if (isSquare(originalWidth, originalHeight) && changeToPortrait) {
    return height;
  }
  if (isPortrait(originalWidth, originalHeight) && changeToPortrait) {
    return width;
  }
  return originalWidth;
};
const convertToV5 = (preparedData) => (width) => (tuning) => ConverterV5(preparedData, width, tuning);
const getTuning = (id) => fetchTuning(id);
const prepareLowResV5Stage = (merch) => (thumb) => (value) => getLowResV5Stage(merch)(value).then((merchData) => {
  const scaledWidth = calcScaledWidth({
    originalWidth: merchData.structure.width,
    originalHeight: merchData.structure.height,
    width: thumb.width,
    height: thumb.height
  });
  return getTuning(merchData.record.id).then((tuningJson) => convertToV5(merchData)(scaledWidth)(tuningJson));
});
const prepareHighResV5Stage = (merch) => (thumb) => (value) => getHighResV5Stage(merch)(value).then((merchData) => {
  const scaledWidth = calcScaledWidth({
    originalWidth: merchData.structure.width,
    originalHeight: merchData.structure.height,
    width: thumb.width,
    height: thumb.height
  });
  return getTuning(merchData.record.id).then((tuningJson) => convertToV5(merchData)(scaledWidth)(tuningJson));
});
const hasChildren = (node, property) => and(
  is$3(Object, node),
  is$3(Array, node[property]),
  node[property].length > 0
);
const treeMapper = (property) => (mapper) => (node, parent) => {
  const mapped = mapper(node, parent);
  if (hasChildren(node, property) === false) {
    return mapped;
  }
  const _parent = [mapped, node];
  mapped[property] = node[property].map((child) => treeMapper(property)(mapper)(child, _parent));
  return mapped;
};
const treeReducer = (property) => (reducer) => (init, node, parent) => {
  const acc = reducer(init, node, parent);
  if (hasChildren(node, property) === false) {
    return acc;
  }
  return node[property].reduce((acc2, item) => {
    return treeReducer(property)(reducer)(acc2, item, node);
  }, acc);
};
const cleanNaN = (n) => isNaN(Number(n)) ? 0 : n;
const scaleY = compose(cleanNaN, (layer, scaled, original) => scaled.height * ((layer.y - original.y) / original.height) + scaled.y);
const scaleX = compose(cleanNaN, (layer, scaled, original) => scaled.width * ((layer.x - original.x) / original.width) + scaled.x);
const getScaleToFitFactor = (destinationWidth, destinationHeight, sourceWidth, sourceHeight) => cleanNaN(Math.min(destinationWidth / sourceWidth, destinationHeight / sourceHeight));
const scaleByFactor = (factor) => (layer) => {
  const x = layer.x * factor;
  const y = layer.y * factor;
  const width = layer.width * factor;
  const height = layer.height * factor;
  return { ...layer, x, y, width, height };
};
const offsetLinealScale = (layer, parent) => {
  let [scaled, original] = parent;
  const { width: scaledWidth, height: scaledHeight } = scaled;
  const { width: originalWidth, height: originalHeight } = original;
  let { width, height, y, x } = { ...layer };
  const scalingFactor = getScaleToFitFactor(scaledWidth, scaledHeight, originalWidth, originalHeight);
  width = cleanNaN(width) * scalingFactor;
  height = cleanNaN(height) * scalingFactor;
  x = scaleX(layer, scaled, original);
  y = scaleY(layer, scaled, original);
  return { ...layer, width, height, x, y };
};
const scaleFromCenter = (layer, parent) => {
  const [scaled] = parent;
  const { width: scaledWidth, height: scaledHeight } = scaled;
  let { width, height, y, x } = layer;
  const scalingFactor = getScaleToFitFactor(scaledWidth, scaledHeight, width, height);
  width = cleanNaN(width) * scalingFactor;
  height = cleanNaN(height) * scalingFactor;
  x = Math.max(0, (scaledWidth - width) / 2);
  y = Math.max(0, (scaledHeight - height) / 2);
  return { ...layer, width, height, x, y };
};
const layersLens = lensProp("layers");
const smartObjectLens = lensProp("smartObject");
const watermarkLens = compose(layersLens, findByNameLens("Watermark"));
const designLens = compose(layersLens, findByNameLens("Design"));
const graphicsLens = compose(designLens, layersLens, findByNameLens("Graphics"));
const textLens = compose(designLens, layersLens, findByNameLens("Text"));
const safeboxesLens = compose(designLens, layersLens, findByNameLens("Safeboxes"));
const backgroundsLens = compose(designLens, layersLens, findByNameLens("Backgrounds"));
const backgroundLens = compose(backgroundsLens, layersLens, findByNameLens("Background"));
const backgroundReplaceLens = compose(backgroundLens, layersLens, findByStarsWithLens("replace"));
const backgroundReplaceSmartObjectLens = compose(backgroundReplaceLens, smartObjectLens);
const backgroundColorLens = compose(designLens, layersLens, findByNameLens("Background Color"));
const systemSquareCheckeredLayerLens = compose(layersLens, findByNameLens("_system.square_checkered_layer"));
const VALUE = Symbol("Value");
const _Structure = class {
  constructor(structure) {
    __privateAdd(this, _update);
    this[VALUE] = structure;
  }
  static of(structure) {
    return new _Structure(structure);
  }
  updateRoot(updater) {
    return __privateMethod(this, _update, update_fn).call(this, lensPath([]), updater);
  }
  updateWatermark(updater) {
    return __privateMethod(this, _update, update_fn).call(this, watermarkLens, updater);
  }
  updateDesign(updater) {
    return __privateMethod(this, _update, update_fn).call(this, designLens, updater);
  }
  updateSafeboxes(updater) {
    return __privateMethod(this, _update, update_fn).call(this, safeboxesLens, updater);
  }
  updateTexts(updater) {
    return __privateMethod(this, _update, update_fn).call(this, textLens, updater);
  }
  updateBackgroundReplaceSmartObject(updater) {
    return __privateMethod(this, _update, update_fn).call(this, backgroundReplaceSmartObjectLens, updater);
  }
  updateBackgroundColor(updater) {
    return __privateMethod(this, _update, update_fn).call(this, backgroundColorLens, updater);
  }
  updateSystemSquareCheckered(updater) {
    return __privateMethod(this, _update, update_fn).call(this, systemSquareCheckeredLayerLens, updater);
  }
  updateGraphics(updater) {
    return __privateMethod(this, _update, update_fn).call(this, graphicsLens, updater);
  }
  getRoot(data) {
    return view(lensPath([]), data != null ? data : this[VALUE]);
  }
  getDesign(data) {
    return view(designLens, data != null ? data : this[VALUE]);
  }
  getSafeboxes(data) {
    return view(safeboxesLens, data != null ? data : this[VALUE]);
  }
  getBackgroundReplaceSmartObject() {
    return view(backgroundReplaceSmartObjectLens, this[VALUE]);
  }
  valueOf() {
    return this[VALUE];
  }
};
let Structure = _Structure;
_update = new WeakSet();
update_fn = function(lens2, updater) {
  if (isNotNil(view(lens2, this[VALUE]))) {
    this[VALUE] = over(lens2, updater, this[VALUE]);
  }
  return this;
};
const deepCopy = (data) => JSON.parse(JSON.stringify(data));
const parentGetter = (fn, structure) => () => [fn(), fn(structure)];
const setDimensions = (width, height) => (layer) => ({ ...layer, height, width });
const offSetLinealScaleMapper = (parentNodeGetter) => (layer) => {
  const parentNode = parentNodeGetter();
  return treeMapper("layers")((layer2, parent) => {
    return parent ? offsetLinealScale(layer2, parent) : offsetLinealScale(layer2, parentNode);
  })(layer);
};
const backgroundSmartObjectUpdater = (width, height) => (smartObject) => {
  return {
    ...smartObject,
    width,
    height,
    transform: {
      ...smartObject == null ? void 0 : smartObject.transform,
      transformPoints: [
        0,
        0,
        width,
        0,
        width,
        height,
        0,
        height
      ]
    }
  };
};
const safeboxesUpdater = (parentNodeGetter) => (layer) => {
  const parentNode = parentNodeGetter();
  return treeMapper("layers")((layer2, parent) => {
    return parent ? offsetLinealScale(layer2, parent) : scaleFromCenter(layer2, parentNode);
  })(layer);
};
const templateScaler = (structure) => (size = {}) => {
  var _a, _b;
  const width = (_a = size.width) != null ? _a : structure.width;
  const height = (_b = size.height) != null ? _b : structure.height;
  if ((width > 0 && height > 0) === false) {
    return structure;
  }
  const structureInstance = Structure.of(deepCopy(structure));
  const getDesign = structureInstance.getDesign.bind(structureInstance);
  const getSafeboxes = structureInstance.getSafeboxes.bind(structureInstance);
  const getRoot = structureInstance.getRoot.bind(structureInstance);
  return structureInstance.updateRoot(setDimensions(width, height)).updateWatermark(setDimensions(width, height)).updateDesign(setDimensions(width, height)).updateSafeboxes(safeboxesUpdater(parentGetter(getDesign, structure))).updateTexts(offSetLinealScaleMapper(parentGetter(getSafeboxes, structure))).updateGraphics(offSetLinealScaleMapper(parentGetter(getDesign, structure))).updateBackgroundReplaceSmartObject(backgroundSmartObjectUpdater(width, height)).updateBackgroundColor(offSetLinealScaleMapper(parentGetter(getDesign, structure))).updateSystemSquareCheckered(offSetLinealScaleMapper(parentGetter(getRoot, structure))).valueOf();
};
const tap = curry(tap$2);
const childProperty = "layers";
const cacheLayer = (store) => (layer) => {
  const { width, height, x, y, name } = layer;
  store[name] = { ...store[name], width, height, x, y };
};
const scalingMapper = (layers) => (factor) => {
  const cachedLayers = clone(layers);
  const cacheResult = (fn) => (...args) => tap(cacheLayer(cachedLayers))(fn(...args));
  return (layer, parent) => {
    const _strategies = {
      Graphic: cacheResult(scaleByFactor(factor))
    };
    const defaultStrategy = scaleByFactor(factor);
    return _strategies[layer.name] ? _strategies[layer.name](layer, parent) : defaultStrategy(layer);
  };
};
const assetScaler = (structure) => ({ width, height }) => {
  const structureClone = clone(structure);
  const scaleFactor = getScaleToFitFactor(Number(width), Number(height), structure.width, structure.height);
  const flatStructure = treeReducer(childProperty)((acc, node, parent) => {
    var _a;
    const { width: width2, height: height2, visible, x, y, name } = node;
    return { ...acc, [name]: { width: width2, height: height2, visible, x, y, name, parent: (_a = parent == null ? void 0 : parent.name) != null ? _a : null } };
  })([], structureClone);
  const mapFunction = scalingMapper(flatStructure)(scaleFactor);
  const mapper = treeMapper(childProperty)(mapFunction);
  return mapper(structureClone);
};
const toJson = (response) => response.json();
const mergerByProperties = mergerRecordsByPropertiesBuilder(strategies$1);
const fetchCleanRecords = (id) => (preset) => fetchRecords$1({ id, preset }).then(([ui, preset2]) => ({ ui: prepare(ui), preset: clean(preset2) }));
const fetchAndMergeRecords = (id) => composeP(mergerByProperties, fetchCleanRecords(id));
const lowStructureFetcher = (sizeId) => sizeId ? fetchLowResStructureBySizeCreator$1(sizeId) : fetchLowResStructure$1;
const highStructureFetcher = (sizeId) => sizeId ? fetchHighResStructureBySizeCreator$1(sizeId) : fetchHighResStructure$1;
const prepareStageData = (recordFetcher) => (structureFetcher) => ({ id, preset, sizeId }) => {
  const source = { id, preset: preset || null };
  return Promise.all([
    recordFetcher(id)(preset).then(mapAssetLayersFromRecord),
    structureFetcher(sizeId)(id).then(toJson)
  ]).then(([record, structure]) => ({ record, structure, source }));
};
const fetchAndPrepareStageData = prepareStageData(fetchAndMergeRecords);
const addFetchToQueue = (structureFetcher) => pushToQueue(fetchAndPrepareStageData(structureFetcher));
const fetchLowResStage = addFetchToQueue(lowStructureFetcher);
const fetchHighResStage = addFetchToQueue(highStructureFetcher);
const fetchLowResStages = (stages) => stages.map((data) => () => fetchLowResStage(data));
const fetchHighResStages = (stages) => stages.map((data) => () => fetchHighResStage(data));
const mergeByTags = (ui, values) => mapValues(ui, values).then((values2) => mergerRecordsByTags(ui, values2));
const fetchRecords = pushToQueue(fetchRecords$1);
const fetchRecord = pushToQueue(fetchRecord$1);
const fetchPreset = pushToQueue(fetchPreset$1);
const fetchLowResStructure = pushToQueue(fetchLowResStructure$1);
const fetchHighResStructure = pushToQueue(fetchHighResStructure$1);
const fetchLowResStructureBySizeCreator = pushToQueue(fetchLowResStructureBySizeCreator$1);
const fetchHighResStructureBySizeCreator = pushToQueue(fetchHighResStructureBySizeCreator$1);
const fetchAssetLayers = pushToQueue(fetchAssetLayers$1);
const fetchAssetStructure = pushToQueue(fetchAssetStructure$1);
const applyValuesToRecord = (values) => (stageData) => {
  const data = JSON.parse(JSON.stringify(stageData));
  return mergeByTags(data.record, values).then((record) => ({ ...data, record }));
};
const _prepareV5StageDataBuilder = (fetcher2) => (stage) => (value) => {
  return isNotEmptyObject(value) ? fetcher2(stage).then(applyValuesToRecord(value)) : fetcher2(stage);
};
const getLowResV5Stage = _prepareV5StageDataBuilder(fetchLowResStage);
const getHighResV5Stage = _prepareV5StageDataBuilder(fetchHighResStage);
const prepareLowResV5Stages = (stages) => (value) => stages.map((stage) => () => prepareLowResV5Data(stage)(value));
const prepareHighResV5Stages = (stages) => (value) => stages.map((stage) => () => prepareHighResV5Data(stage)(value));
const fetchAndScaleStructureBuilder = (fetcher2) => (id) => {
  const onceFetcher = once$1(composeP(templateScaler, toJson, fetcher2));
  return (size) => onceFetcher(id).then((scaler) => scaler(size));
};
const lowStructureScaler = fetchAndScaleStructureBuilder(fetchLowResStructure);
const highStructureScaler = fetchAndScaleStructureBuilder(fetchHighResStructure);
export {
  assetScaler,
  fetchAndScaleStructureBuilder,
  fetchAssetLayers,
  fetchAssetStructure,
  fetchHighResStage,
  fetchHighResStages,
  fetchHighResStructure,
  fetchHighResStructureBySizeCreator,
  fetchLowResStage,
  fetchLowResStages,
  fetchLowResStructure,
  fetchLowResStructureBySizeCreator,
  mergeRecords as fetchMergedRecords,
  fetchPreset,
  fetchRecord,
  fetchRecords,
  getHighResV5Stage,
  getLowResV5Stage,
  getScaleToFitFactor,
  highStructureScaler,
  lowStructureScaler,
  mergeByTags,
  mergerByProperties,
  prepareHighResV5Stage,
  prepareHighResV5Stages,
  prepareLowResV5Stage,
  prepareLowResV5Stages,
  setup,
  templateScaler
};
